import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { loadErrorMessage, loadList } from '../../utils/loadState'
import { getExportTemplates, uploadExportTemplate, getExportTemplate, deleteExportTemplate, getExportTemplateDownloadUrl, uploadExportTemplateVersion, getVendors } from '../../api'
import MappingPreview from './MappingPreview'
import './MappingPreview.css'
import './ExportTemplates.css'

const FORMAT_ICONS = {
  docx: { label: 'W', cls: 'et-icon-docx' },
  xlsx: { label: 'X', cls: 'et-icon-xlsx' },
  pptx: { label: 'P', cls: 'et-icon-pptx' },
  pdf:  { label: 'P', cls: 'et-icon-pdf' },
}

const CATEGORIES = ['Client Deliverable', 'Gap Analysis', 'Migration Report', 'Compliance Audit', 'Vendor Specific Template', 'Workplan', 'Custom']
const VISIBILITY_OPTIONS = ['My organization', 'All Customers', 'Specific customers']
const FILTER_PILLS = ['All', 'Word', 'Excel', 'PowerPoint', 'System', 'My Templates']

function formatExt(filename) {
  if (!filename) return 'docx'
  const ext = filename.split('.').pop().toLowerCase()
  if (['docx', 'doc'].includes(ext)) return 'docx'
  if (['xlsx', 'xls'].includes(ext)) return 'xlsx'
  if (['pptx', 'ppt'].includes(ext)) return 'pptx'
  if (ext === 'pdf') return 'pdf'
  return 'docx'
}

function formatSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function timeAgo(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const now = new Date()
  const diff = now - d
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/* ─── UPLOAD MODAL ─── */
function UploadModal({ onClose, onUploaded }) {
  const fileRef = useRef()
  const [file, setFile] = useState(null)
  const [name, setName] = useState('')
  const [category, setCategory] = useState('Client Deliverable')
  const [visibility, setVisibility] = useState('My organization')
  const [description, setDescription] = useState('')
  const [placeholders, setPlaceholders] = useState([])
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [vendors, setVendors] = useState([])
  const [vendorId, setVendorId] = useState('')
  const [vendorsError, setVendorsError] = useState(null)

  async function loadVendors() {
    const { items, error } = await loadList(getVendors, 'vendors')
    setVendors(items || [])
    setVendorsError(error)
  }

  useEffect(() => { loadVendors() }, [])

  function handleFile(f) {
    if (!f) return
    setFile(f)
    setName(f.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' '))
    // Scan for {{placeholders}} if we had the content — for now show empty
    // In real implementation, backend scans the file
    setPlaceholders([])
  }

  function handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    const f = e.dataTransfer?.files?.[0]
    if (f) handleFile(f)
  }

  async function handleUpload() {
    if (!file || !name.trim()) return
    setUploading(true)
    setError(null)
    try {
      const result = await uploadExportTemplate(file, {
        name: name.trim(),
        category,
        visibility,
        description: description.trim(),
        vendor_id: category === 'Vendor Specific Template' ? vendorId : '',
      })
      onUploaded(result)
    } catch (err) {
      setError(err.message || 'Upload failed')
      setUploading(false)
    }
  }

  return (
    <div className="et-modal-overlay" onClick={onClose}>
      <div className="et-modal" onClick={e => e.stopPropagation()}>
        <div className="et-modal-header">
          <div className="et-modal-title">Upload Template</div>
          <button className="et-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="et-modal-body">
          {!file ? (
            <div
              className="et-upload-zone"
              onClick={() => fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('dragging') }}
              onDragLeave={e => e.currentTarget.classList.remove('dragging')}
              onDrop={handleDrop}
            >
              <div className="et-uz-icon">📄</div>
              <div className="et-uz-text">Drop your template here or <strong>click to browse</strong></div>
              <div className="et-uz-hint">.docx, .xlsx, or .pptx · Max 25MB · Use {'{{field_name}}'} for placeholders</div>
              <input
                ref={fileRef}
                type="file"
                accept=".docx,.xlsx,.pptx,.doc,.xls,.ppt,.pdf"
                style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files?.[0])}
              />
            </div>
          ) : (
            <div className="et-file-preview">
              <div className={`et-fp-icon ${FORMAT_ICONS[formatExt(file.name)]?.cls || 'et-icon-docx'}`}>
                {FORMAT_ICONS[formatExt(file.name)]?.label || 'W'}
              </div>
              <div className="et-fp-info">
                <div className="et-fp-name">{file.name}</div>
                <div className="et-fp-meta">
                  {formatExt(file.name).toUpperCase()} document · {formatSize(file.size)}
                  {placeholders.length > 0 && ` · ${placeholders.length} placeholders detected`}
                </div>
              </div>
              <button className="et-fp-rm" onClick={() => { setFile(null); setPlaceholders([]) }}>✕</button>
            </div>
          )}

          <div className="et-form-group">
            <label className="et-form-label">Template Name</label>
            <input
              type="text"
              className="et-form-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Client Gap Analysis Report"
            />
          </div>

          <div className="et-form-row">
            <div className="et-form-group">
              <label className="et-form-label">Category</label>
              <select className="et-form-select" value={category} onChange={e => { setCategory(e.target.value); if (e.target.value !== 'Vendor Specific Template') setVendorId('') }}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="et-form-group">
              <label className="et-form-label">Visibility</label>
              <select className="et-form-select" value={visibility} onChange={e => setVisibility(e.target.value)}>
                {VISIBILITY_OPTIONS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
          </div>

          {category === 'Vendor Specific Template' && (
            <div className="et-form-group">
              <label className="et-form-label">Vendor</label>
              <select className="et-form-select" value={vendorId} onChange={e => setVendorId(e.target.value)}>
                <option value="">Select a vendor…</option>
                {vendors.map(v => <option key={v.id || v.vendor_id} value={v.id || v.vendor_id}>{v.name || v.vendor_name}</option>)}
              </select>
              {vendorsError && <LoadError inline what="vendors" error={vendorsError} onRetry={loadVendors} />}
            </div>
          )}

          <div className="et-form-group">
            <label className="et-form-label">Description <span className="et-optional">(optional)</span></label>
            <textarea
              className="et-form-textarea"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What is this template used for?"
            />
          </div>

          {placeholders.length > 0 && (
            <div className="et-ph-info-box">
              <strong>{placeholders.length} placeholders detected</strong> — Sempirical will auto-map these to analysis data when you export.
              <div className="et-ph-tags">
                {placeholders.map(p => <span key={p} className="et-ph-tag">{`{{${p}}}`}</span>)}
              </div>
            </div>
          )}

          {error && <div className="et-error">{error}</div>}
        </div>
        <div className="et-modal-footer">
          <button className="et-btn et-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="et-btn et-btn-primary"
            onClick={handleUpload}
            disabled={!file || !name.trim() || uploading || (category === 'Vendor Specific Template' && !vendorId)}
          >
            {uploading ? 'Uploading…' : 'Upload Template'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── TEMPLATE CARD ─── */
function TemplateCard({ template, onClick }) {
  const ext = formatExt(template.filename || template.format || 'docx')
  const icon = FORMAT_ICONS[ext] || FORMAT_ICONS.docx
  const badge = template.source === 'system' ? 'system'
    : template.shared ? 'shared'
    : 'custom'

  return (
    <div className="et-card" onClick={() => onClick(template)}>
      <button className="et-card-menu" onClick={e => e.stopPropagation()}>⋯</button>
      <div className="et-card-top">
        <div className={`et-card-icon ${icon.cls}`}>{icon.label}</div>
        <div className="et-card-info">
          <div className="et-card-name">{template.name || template.template_id}</div>
          <div className="et-card-desc">{template.description || 'No description'}</div>
        </div>
      </div>
      <div className="et-card-meta">
        <span className={`et-badge ${badge}`}>{badge}</span>
        <span className="et-dot" />
        <span>{ext.toUpperCase()}</span>
        {template.placeholder_count > 0 && (
          <>
            <span className="et-dot" />
            <span>{template.placeholder_count} placeholders</span>
          </>
        )}
        {template.usage_count > 0 && (
          <>
            <span className="et-dot" />
            <span>Used {template.usage_count}×</span>
          </>
        )}
      </div>
    </div>
  )
}

/* ─── TEMPLATE DETAIL ─── */
function TemplateDetail({ templateId, onBack }) {
  const [template, setTemplate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showMapping, setShowMapping] = useState(false)
  // A failed load is an error with a Retry. It used to render a made-up template with no
  // placeholders, usage or versions -- a failure dressed up as an empty template (item 201).
  const [loadError, setLoadError] = useState(null)

  async function load() {
    setLoading(true)
    setLoadError(null)
    try {
      const data = await getExportTemplate(templateId)
      // Backend returns template directly for uploaded, or {template: {...}} for predefined
      setTemplate(data.template || data)
    } catch (err) {
      setTemplate(null)
      setLoadError(loadErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [templateId])

  if (loading) return <div className="et-loading"><div className="et-spinner" /><p>Loading template…</p></div>
  if (loadError) return (
    <div className="et-detail">
      <button className="et-back" onClick={onBack}>← Back to templates</button>
      <LoadError what="this template" error={loadError} onRetry={load} />
    </div>
  )

  const t = template
  const ext = formatExt(t.filename || t.output_format || t.format || 'docx')
  const icon = FORMAT_ICONS[ext] || FORMAT_ICONS.docx
  const placeholders = t.placeholders || []
  const usage = t.usage || []
  const versions = t.versions || []
  const badge = t.source === 'system' ? 'system' : t.shared ? 'shared' : 'custom'

  return (
    <div className="et-detail">
      <button className="et-back" onClick={onBack}>← Back to templates</button>
      <div className="et-detail-layout">
        <div className="et-detail-main">
          {/* Header card */}
          <div className="et-detail-header">
            <div className={`et-detail-icon ${icon.cls}`}>{icon.label}</div>
            <div className="et-detail-info">
              <div className="et-detail-name">{t.name}</div>
              <div className="et-detail-desc">{t.description || 'No description'}</div>
              <div className="et-detail-meta">
                <span className={`et-badge ${badge}`}>{badge}</span>
                <span>{ext.toUpperCase()}</span>
                {t.size && <span>{formatSize(t.size)}</span>}
                {t.created_at && <span>Uploaded {timeAgo(t.created_at)}</span>}
              </div>
            </div>
            <div className="et-detail-actions">
              <button className="et-btn et-btn-primary et-btn-sm" onClick={() => setShowMapping(true)}>
                Map & Export
              </button>
              <button className="et-btn et-btn-secondary et-btn-sm">Re-upload</button>
              <button className="et-btn et-btn-sm et-btn-danger-ghost" onClick={async () => {
                if (!window.confirm(`Delete template "${t.name}"?`)) return
                try {
                  await deleteExportTemplate(templateId)
                  onBack()
                } catch (err) {
                  alert('Delete failed: ' + (err.message || 'Unknown error'))
                }
              }}>Delete</button>
            </div>
          </div>

          {/* Placeholders */}
          <div className="et-section">
            <div className="et-section-header">
              <div className="et-section-title">Detected Placeholders ({placeholders.length})</div>
            </div>
            <div className="et-section-body">
              {placeholders.length > 0 ? (
                <>
                  <div className="et-ph-explain">
                    Sempirical scanned your template and found these placeholders. They'll be automatically mapped to analysis data when you use this template during export.
                  </div>
                  <div className="et-ph-grid">
                    {placeholders.map(p => (
                      <span key={p} className="et-ph-tag">{`{{${p}}}`}</span>
                    ))}
                  </div>
                  <div className="et-ph-note">
                    <strong>How mapping works:</strong> When you select this template during a Work Effort export, Sempirical will match each placeholder to the available analysis results. Standard names like <code>{'{{customer_name}}'}</code> and <code>{'{{findings_table}}'}</code> are auto-mapped. Custom names can be mapped manually at export time.
                  </div>
                </>
              ) : (
                <div className="et-ph-explain">
                  No placeholders detected. You can add {'{{placeholder_name}}'} markers in your template file and re-upload.
                </div>
              )}
            </div>
          </div>

          {/* Sheets (for Excel files) */}
          {(t.sheets || []).length > 0 && (
            <div className="et-section">
              <div className="et-section-header">
                <div className="et-section-title">Sheets ({t.sheets.length})</div>
              </div>
              <div className="et-section-body">
                <div className="et-ph-explain" style={{ marginBottom: 12 }}>
                  This workbook contains {t.sheets.length} sheet{t.sheets.length > 1 ? 's' : ''}. Each sheet maps to a separate data conversion tab at export time.
                </div>
                {t.sheets.map((s, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', border: '1px solid var(--gray-200)',
                    borderRadius: 8, marginBottom: 6, background: 'var(--white)'
                  }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: 6, background: 'var(--green-light)',
                      color: 'var(--green-dark)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0
                    }}>{i + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--gray-400)' }}>
                        {s.columns} column{s.columns !== 1 ? 's' : ''}
                        {s.column_names?.length > 0 && ` · ${s.column_names.slice(0, 5).join(', ')}${s.columns > 5 ? '…' : ''}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Usage */}
          <div className="et-section">
            <div className="et-section-header">
              <div className="et-section-title">Recent Usage</div>
            </div>
            <div className="et-section-body">
              {usage.length > 0 ? usage.map((u, i) => (
                <div key={i} className="et-usage-item">
                  <div className="et-usage-avatar" style={{ background: u.color || '#b45309' }}>
                    {(u.customer_name || '??').substring(0, 2).toUpperCase()}
                  </div>
                  <div className="et-usage-info">
                    <div className="et-usage-title">{u.title || 'Export'}</div>
                    <div className="et-usage-meta">
                      {u.customer_name}
                      {u.pages && ` · ${u.pages} pages`}
                      {u.mapped_count != null && ` · ${u.mapped_count}/${placeholders.length} placeholders mapped`}
                    </div>
                  </div>
                  <div className="et-usage-date">{timeAgo(u.date)}</div>
                </div>
              )) : (
                <div className="et-empty-usage">
                  <div className="et-empty-icon">📋</div>
                  <div className="et-empty-text">
                    This template hasn't been used yet.<br />
                    It'll appear as an option when you export from a Work Effort.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="et-detail-sidebar">
          <div className="et-sidebar-card">
            <div className="et-sc-title">Template Details</div>
            <div className="et-sc-row"><span className="et-sc-label">Format</span><span className="et-sc-value">{ext.toUpperCase()}</span></div>
            {t.size && <div className="et-sc-row"><span className="et-sc-label">Size</span><span className="et-sc-value">{formatSize(t.size)}</span></div>}
            <div className="et-sc-row"><span className="et-sc-label">Placeholders</span><span className="et-sc-value">{placeholders.length} detected</span></div>
            <div className="et-sc-row"><span className="et-sc-label">Category</span><span className="et-sc-value">{t.category || '—'}</span></div>
            <div className="et-sc-row"><span className="et-sc-label">Visibility</span><span className="et-sc-value">{t.visibility || 'Just me'}</span></div>
            <div className="et-sc-row"><span className="et-sc-label">Times used</span><span className="et-sc-value">{t.usage_count || 0}</span></div>
            {t.last_used_at && <div className="et-sc-row"><span className="et-sc-label">Last used</span><span className="et-sc-value">{timeAgo(t.last_used_at)}</span></div>}
            <div className="et-sc-row"><span className="et-sc-label">Uploaded by</span><span className="et-sc-value">{t.uploaded_by || 'Unknown'}</span></div>
          </div>

          <div className="et-sidebar-card">
            <div className="et-sc-title">Version History</div>
            {versions.length > 0 ? versions.map((v, i) => (
              <div key={i} className="et-version-item">
                <div className={`et-version-dot ${i === 0 ? 'current' : 'old'}`} />
                <div className="et-version-info">
                  <div className="et-version-label">
                    {v.label || `v${versions.length - i}`}
                    {i === 0 && <span className="et-version-tag">Current</span>}
                  </div>
                  {v.note && <div className="et-version-meta">{v.note}</div>}
                  <div className="et-version-meta">{v.date} · {v.author}</div>
                </div>
              </div>
            )) : (
              <div className="et-version-item">
                <div className="et-version-dot current" />
                <div className="et-version-info">
                  <div className="et-version-label">v1 <span className="et-version-tag">Current</span></div>
                  <div className="et-version-meta">Initial upload</div>
                  {t.created_at && <div className="et-version-meta">{timeAgo(t.created_at)}</div>}
                </div>
              </div>
            )}
          </div>

          <button className="et-btn et-btn-secondary et-btn-full" onClick={() => {
            window.open(getExportTemplateDownloadUrl(templateId), '_blank')
          }}>Download Original Template</button>
          <button className="et-btn et-btn-secondary et-btn-full" style={{ marginTop: 8 }} onClick={() => {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = '.docx,.doc,.xlsx,.xls,.pptx,.ppt,.pdf'
            input.onchange = async (e) => {
              const f = e.target.files?.[0]
              if (!f) return
              try {
                await uploadExportTemplateVersion(templateId, f)
                window.location.reload()
              } catch (err) {
                alert('Upload failed: ' + (err.message || 'Unknown error'))
              }
            }
            input.click()
          }}>Upload New Version</button>
        </div>
      </div>

      {showMapping && (
        <MappingPreview
          templateId={templateId}
          templateName={t.name}
          onClose={() => setShowMapping(false)}
          onExport={(result) => {
            setShowMapping(false)
            if (result?.download_url) {
              const base = import.meta.env.VITE_API_URL || 'http://localhost:8000'
              window.open(`${base}${result.download_url}`, '_blank')
            }
          }}
        />
      )}
    </div>
  )
}

/* ─── MAIN PAGE ─── */
export default function ExportTemplates() {
  const { templateId } = useParams()
  const navigate = useNavigate()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [showUpload, setShowUpload] = useState(false)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('All')
  const [toast, setToast] = useState(null)
  // A failed load is an error with a Retry, never an empty template grid (item 201).
  const [loadError, setLoadError] = useState(null)

  useEffect(() => {
    loadTemplates()
  }, [templateId])

  async function loadTemplates() {
    const { items, error } = await loadList(getExportTemplates, 'templates')
    setTemplates(items || [])
    setLoadError(error)
    setLoading(false)
  }

  function handleUploaded(result) {
    setShowUpload(false)
    setToast('Template uploaded successfully')
    setTimeout(() => setToast(null), 4000)
    loadTemplates()
    if (result?.template_id) {
      navigate(`/exports/templates/${result.template_id}`)
    }
  }

  function handleCardClick(t) {
    navigate(`/exports/templates/${t.template_id || t.name}`)
  }

  // If viewing a specific template
  if (templateId) {
    return (
      <>
        <TopNav breadcrumbs={[
          { label: 'Export Templates', href: '/exports/templates' },
          { label: templateId },
        ]} />
        <div className="et-page">
          <TemplateDetail
            templateId={templateId}
            onBack={() => navigate('/exports/templates')}
          />
        </div>
      </>
    )
  }

  // Filter templates
  const filtered = templates.filter(t => {
    if (search) {
      const q = search.toLowerCase()
      if (!(t.name || '').toLowerCase().includes(q) && !(t.description || '').toLowerCase().includes(q)) return false
    }
    if (filter === 'Word') return formatExt(t.filename || t.format) === 'docx'
    if (filter === 'Excel') return formatExt(t.filename || t.format) === 'xlsx'
    if (filter === 'PowerPoint') return formatExt(t.filename || t.format) === 'pptx'
    if (filter === 'System') return t.source === 'system'
    if (filter === 'My Templates') return t.source !== 'system'
    return true
  })

  const breadcrumbs = [{ label: 'Export Templates' }]

  if (loading) {
    return (
      <>
        <TopNav breadcrumbs={breadcrumbs} />
        <div className="et-loading"><div className="et-spinner" /><p>Loading templates…</p></div>
      </>
    )
  }

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} />
      {toast && <div className="et-toast">✓ {toast}</div>}
      <div className="et-page">
        <div className="et-header">
          <h1 className="et-title">Export Templates</h1>
          <button className="et-btn et-btn-primary" onClick={() => setShowUpload(true)}>+ Upload Template</button>
        </div>
        <p className="et-subtitle">
          Manage your deliverable templates. Upload Word, Excel, or PowerPoint files with {'{{placeholders}}'} and Sempirical maps them to analysis results at export time.
        </p>

        <div className="et-controls">
          <input
            type="text"
            className="et-search"
            placeholder="Search templates..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="et-filters">
            {FILTER_PILLS.map(f => (
              <button
                key={f}
                className={`et-filter-pill${filter === f ? ' active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {loadError && <LoadError what="templates" error={loadError} onRetry={loadTemplates} />}

        <div className="et-grid">
          {filtered.map((t, i) => (
            <TemplateCard key={t.template_id || i} template={t} onClick={handleCardClick} />
          ))}
        </div>

        {filtered.length === 0 && templates.length > 0 && (
          <div className="et-no-results">No templates match your search.</div>
        )}
      </div>

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUploaded={handleUploaded} />}
    </>
  )
}

